import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, pollJob } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Field } from "@/components/common";

export const SUGGESTIONS = [
  ["Generate qualified leads", "Generate 100 qualified Canadian small-business leads for AccruallQ over the next 30 days."],
  ["Launch a product", "Launch our new product to existing customers and prospects in our core market within 6 weeks."],
  ["Promote an offer", "Promote our free Corporate Health Check offer to small-business owners this month."],
  ["Build brand awareness", "Build brand awareness among founders in Canada and the United States over the next quarter."],
  ["Create a 30-day campaign", "Create a 30-day campaign that turns website visitors into booked demos."],
  ["Re-engage an audience", "Re-engage trial users who signed up but never activated in the last 90 days."],
];
const CHANNELS = ["LinkedIn", "Email", "Search", "Blog / SEO", "Meta", "X", "YouTube", "Partner webinars"];
const EMPTY = { objective: "", target_audience: "", offer: "", market: "", duration: "", channels: [], budget: "", conversion_action: "", instructions: "" };
const FIELDS = [["objective", "Objective"], ["target_audience", "Target audience"], ["offer", "Offer"], ["market", "Geographic market"],
  ["duration", "Duration"], ["budget", "Budget"], ["conversion_action", "Primary conversion action"]];

export default function CommandPanel({ onInterpreted, autoFocus }) {
  const { path, can } = useWorkspace();
  const [command, setCommand] = useState("");
  const [inputs, setInputs] = useState(EMPTY);
  const [showMore, setShowMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);

  const submit = async (e) => {
    e?.preventDefault();
    if (command.trim().length < 5) return toast.error("Describe the outcome you want to achieve.");
    setBusy(true);
    try {
      const { data } = await api.post(path("/command/interpret"), { command, ...inputs });
      const result = await pollJob(path(`/jobs/${data.job_id}`));
      onInterpreted({ command, interpretation: result });
    } catch (e2) { toast.error(e2.response ? apiError(e2) : e2.message); } finally { setBusy(false); }
  };
  const toggleCh = (c) => setInputs((s) => ({ ...s, channels: s.channels.includes(c) ? s.channels.filter((x) => x !== c) : [...s.channels, c] }));

  return (
    <form onSubmit={submit} data-testid="command-panel" className="relative cq-surface cq-command-glow p-5 sm:p-8 overflow-hidden">
      <div className="absolute inset-0 cq-grid-bg opacity-40 pointer-events-none" />
      {busy && <div className="absolute top-0 left-0 h-px w-1/3 cq-scan" style={{ background: "var(--cq-gradient)" }} />}
      <div className="relative">
        <div className="cq-label mb-3">Command</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#F7FAFF]">What do you want to achieve?</h2>
        <div className="mt-5 relative">
          <span className="absolute left-4 top-4 font-mono text-[#22E6F2] select-none">›</span>
          <textarea ref={ref} data-testid="command-input-field" rows={3} disabled={!can("edit")} value={command} onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            placeholder="e.g. Generate 100 qualified Canadian small-business leads over the next 30 days."
            className="cq-input !bg-[#050816] !pl-9 !py-3.5 !text-[15px] !rounded-xl resize-none" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="suggested-commands">
          {SUGGESTIONS.map(([label, text], i) => (
            <button type="button" key={label} disabled={!can("edit")} data-testid={`suggested-command-${i}`} onClick={() => setCommand(text)}
              className="px-3 py-1.5 rounded-full border border-[#243153] text-xs text-[#AAB6CC] hover:text-[#F7FAFF] hover:border-[#22E6F2]/50 transition-colors">{label}</button>
          ))}
        </div>
        {showMore && (
          <div data-testid="structured-inputs" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 cq-rise">
            {FIELDS.map(([k, label]) => (
              <Field key={k} label={label}><input data-testid={`command-input-${k}`} className="cq-input !py-2" value={inputs[k]} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} /></Field>
            ))}
            <div className="sm:col-span-2 lg:col-span-4">
              <span className="cq-muted-label block mb-2">Channels</span>
              <div className="flex flex-wrap gap-2">{CHANNELS.map((c) => (
                <button type="button" key={c} data-testid={`command-channel-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => toggleCh(c)}
                  className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${inputs.channels.includes(c) ? "border-[#22E6F2]/60 text-[#22E6F2] bg-[#22E6F2]/10" : "border-[#243153] text-[#AAB6CC] hover:text-[#F7FAFF]"}`}>{c}</button>))}
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <Field label="Additional instructions"><textarea data-testid="command-input-instructions" rows={2} className="cq-input" value={inputs.instructions} onChange={(e) => setInputs({ ...inputs, instructions: e.target.value })} /></Field>
            </div>
          </div>
        )}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3">
          <button type="button" data-testid="command-toggle-details" onClick={() => setShowMore(!showMore)} className="inline-flex items-center gap-2 text-sm text-[#AAB6CC] hover:text-[#F7FAFF]">
            <SlidersHorizontal size={14} /> {showMore ? "Hide details" : "Add structured details (optional)"} <ChevronDown size={14} className={showMore ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
          {can("edit") ? <button data-testid="command-submit-button" disabled={busy} className="cq-btn cq-btn-primary !px-5 !py-2.5">
            {busy ? <><Loader2 size={15} className="animate-spin" /> Interpreting command…</> : <>Interpret command <ArrowRight size={15} /></>}
          </button> : <span data-testid="command-no-permission" className="text-xs text-[#7C8BAD]">Your role is view-only. An editor can turn this into a campaign.</span>}
        </div>
      </div>
    </form>
  );
}
